import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import SessionsChartContainer from './Chart/SessionsChartContainer';
import RobotMessageComponent from '../../Components/RobotMessageComponent/RobotMessageComponent';
import './SessionsContainer.css';
import SelectedRecord from '../../Components/SelectedRecord/SelectedRecord';
import moment from 'moment-timezone';
import SessionsStats from './Stats/SessionsStats';
import SessionsDateRangeSelector from './Filters/SessionsDateRangeSelector';
import { actions as sessionsActions } from '../../Reducks/Sessions';
import SessionsRangeFilter from './Filters/SessionsRangeFilter';
import Tips from '../../Components/Tips/Tips';
import { desktopSkeletonScreen, mobileSkeletonScreen } from '../../SkeletonScreens/SkeletonScreen';
import { SESSIONS_OVERVIEW } from '../../Telemetry/Mixpanel/Constants/mixpanelConstants';
import { RobotMessageState } from '../../Utils/Constants';
import SessionsTable from './Table/SessionsTable';
import AppConstants from '../../AppConstants';

export class SessionsContainer extends Component {
  static propTypes = {
    reduced_sessions: PropTypes.object,
    tips: PropTypes.array,
    isBig: PropTypes.bool
  };

  static contextTypes = {
    t: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      focusedSessionKeyInTable: null,
      focusedSessionKeyInChart: null
    };
  }

  render() {
    const { sessions, fetching, error } = this.props.reduced_sessions;
    const isEmptyOrError = (sessions.allKeys && sessions.allKeys.length === 0 && !fetching) || error;
    const { isBig } = this.props;
    if (fetching) {
      if (isBig) {
        return <div className="sessionsContainer">{desktopSkeletonScreen()}</div>;
      } else {
        return <div className="sessionsContainer">{mobileSkeletonScreen()}</div>;
      }
    }
    return (
      <div className="sessionsContainer">
        {isBig && this.dateAndFilterRow()}
        {!isBig && this.selectedRecordRow()}
        {this.statsCardsRow()}
        {isBig && AppConstants.TIPS_ENABLED && this.tipsRow()}
        {isEmptyOrError ? (
          <RobotMessageComponent type={error ? RobotMessageState.ERROR_STATE : RobotMessageState.EMPTY_STATE} />
        ) : (
          <div className="chartAndTable">
            {this.chartRow()}
            {this.tableRow()}
          </div>
        )}
      </div>
    );
  }

  dateAndFilterRow = () => {
    const mixpanelData = {
      action: SESSIONS_OVERVIEW
    };
    const { t } = this.context;
    const { reduced_sessions, getSessions, filterSessionsByCount } = this.props;
    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="overviewHeading">{t('header.overview')}</div>
        <div className="d-flex justify-content-between align-items-center spreadHorizontalItems-8">
          <SessionsRangeFilter
            isBig={true}
            uniqueId={'sessionsFilterInSessionsContainer'}
            mixpanelData={mixpanelData}
            reduced_sessions={reduced_sessions}
            filterSessionsByCount={filterSessionsByCount}
          />
          <SessionsDateRangeSelector
            isBig={true}
            uniqueId={'sessionsDateRangeSelectorInContainer'}
            mixpanelData={mixpanelData}
            reduced_sessions={reduced_sessions}
            getSessions={getSessions}
          />
        </div>
      </div>
    );
  };

  selectedRecordRow = () => {
    const { t } = this.context;
    return (
      <SelectedRecord
        defaultTitle={t('header.overview')}
        defaultSubTitle={this.getSelectedDateRangeString()}
        title={this.getSelectedWebinarName()}
        subTitle={this.getSelectedWebinarStartTime()}
      />
    );
  };

  statsCardsRow = () => {
    const { sessions, stats, trend, filter } = this.props.reduced_sessions;
    const totalSessions = sessions.filteredKeys.length;
    const selectedSessionsCount = sessions.filteredSelectedSessionKeys.length;
    const mixpanelData = {
      action: SESSIONS_OVERVIEW
    };
    const { isBig } = this.props;
    return (
      <SessionsStats
        isBig={isBig}
        totalSessions={totalSessions}
        totalAttendees={stats.filteredSelectedTotalAttendees}
        totalRegistrants={stats.filteredSelectedTotalRegistrants}
        selectedSessionsCount={selectedSessionsCount}
        selectedSession={sessions.byKey[this.state.focusedSessionKeyInTable]}
        trendData={trend}
        filter={filter}
        mixpanelData={mixpanelData}
      />
    );
  };
  tipsRow = () => {
    const mixpanelData = {
      action: SESSIONS_OVERVIEW
    };
    return <Tips contents={this.props.tips} mixpanelData={mixpanelData} />;
  };

  chartRow = () => {
    const { isBig, reduced_sessions } = this.props;
    return (
      <SessionsChartContainer
        focusSessionInTable={this.focusSessionInTable}
        focusedSessionKeyInChart={this.state.focusedSessionKeyInChart}
        isBig={isBig}
        sessions={reduced_sessions}
      />
    );
  };
  tableRow = () => {
    const mixpanelData = {
      action: SESSIONS_OVERVIEW
    };
    const { isBig } = this.props;
    const { focusedSessionKeyInTable } = this.state;
    return (
      <SessionsTable
        uniqueId={'sesssionsTableInOverview'}
        isBig={isBig}
        sessionData={this.props.reduced_sessions.sessions}
        includeEngagementColumns={false}
        focusedSessionKeyInTable={focusedSessionKeyInTable}
        focusSessionInChart={this.focusSessionInChart}
        mixpanelData={mixpanelData}
      />
    );
  };

  getSelectedWebinarName = () => {
    const { sessions } = this.props.reduced_sessions;
    const { focusedSessionKeyInTable } = this.state;
    return focusedSessionKeyInTable ? sessions.byKey[focusedSessionKeyInTable].webinarName : null;
  };
  getSelectedWebinarStartTime = () => {
    const { t } = this.context;
    const { sessions } = this.props.reduced_sessions;
    const { focusedSessionKeyInTable } = this.state;
    const { startTime, timeZone } = focusedSessionKeyInTable ? sessions.byKey[focusedSessionKeyInTable] : {};
    if (startTime && timeZone) {
      return moment(startTime)
        .tz(timeZone)
        .format(t('formatting.dayDateTimeWithZone'));
    }
    return null;
  };

  getSelectedDateRangeString = () => {
    const { t } = this.context;
    const { fromTime, toTime } = this.props.reduced_sessions.filter;
    if (fromTime && toTime) {
      return `${moment(fromTime).format(t('formatting.date'))} - ${moment(toTime).format(t('formatting.date'))}`;
    }
    return null;
  };

  focusSessionInTable = sessionKey => {
    this.setState({ focusedSessionKeyInTable: sessionKey });
  };
  focusSessionInChart = sessionKey => {
    this.setState({ focusedSessionKeyInChart: sessionKey });
  };
}

export default connect(
  state => ({
    reduced_sessions: state.reduced_sessions
  }),
  { getSessions: sessionsActions.getSessions, filterSessionsByCount: sessionsActions.filterSessionsByCount }
)(SessionsContainer);



// WEBPACK FOOTER //
// ./src/GoToWebinar/Sessions/SessionsContainer.js